import React, { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { Button, ButtonProps } from 'primereact/button';
import { FileUpload, FileUploadProps } from 'primereact/fileupload';
import { canPerformFileAction, FileAction, requireFileAction } from '../../services/menu/file-action.service';
import { FileActionMenuContext } from './file-action-menu-context';

function useFileAction(action: FileAction, menuId?: number) {
  const routedMenuId = useContext(FileActionMenuContext);
  menuId = menuId ?? routedMenuId;
  const [allowed, setAllowed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const pathname = window.location.pathname;
  useEffect(() => {
    let active = true;
    const refresh = () => {
      void canPerformFileAction(action, pathname, menuId).then(value => { if (active) setAllowed(value); })
        .catch(() => { if (active) setAllowed(false); });
    };
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('filePermissionsChanged', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('filePermissionsChanged', refresh);
    };
  }, [action, pathname, menuId]);

  const execute = async (operation: () => void | Promise<unknown>) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      await requireFileAction(action, pathname, menuId);
      await operation();
    } catch {
      setError(`Unable to ${action}. Permission may have changed; refresh and try again.`);
      setAllowed(false);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return { allowed, busy, error, execute };
}

type ActionProps = { action: FileAction; menuId?: number };
export function FileActionButton({ action, menuId, disabled, onClick, ...props }: ButtonProps & ActionProps) {
  const access = useFileAction(action, menuId);
  return <>
    <Button {...props} disabled={disabled || !access.allowed || access.busy}
      onClick={event => { event.stopPropagation(); event.persist(); void access.execute(() => onClick?.(event)); }} />
    {access.error && <span role="alert" className="text-red-600 text-sm">{access.error}</span>}
  </>;
}

export function FileActionNativeButton({ action, menuId, disabled, onClick, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & ActionProps) {
  const access = useFileAction(action, menuId);
  return <>
    <button {...props} disabled={disabled || !access.allowed || access.busy}
      onClick={event => { event.stopPropagation(); event.persist(); void access.execute(() => onClick?.(event)); }} />
    {access.error && <span role="alert" className="text-red-600 text-sm">{access.error}</span>}
  </>;
}

export const FileActionUpload = forwardRef<FileUpload, FileUploadProps & { menuId?: number }>(
  ({ menuId, disabled, uploadHandler, ...props }, ref) => {
    const access = useFileAction('import', menuId);
    return <>
      <FileUpload {...props} ref={ref} disabled={disabled || !access.allowed || access.busy}
        uploadHandler={event => { void access.execute(() => uploadHandler?.(event)); }} />
      {access.error && <span role="alert" className="text-red-600 text-sm">{access.error}</span>}
    </>;
  }
);
FileActionUpload.displayName = 'FileActionUpload';
